import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Spinner, Alert,
    InputGroup, Badge, Tooltip, OverlayTrigger
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function NouveauReglement() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const factureId = searchParams.get('facture');
    const clientId = searchParams.get('client');
    
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(!!factureId || !!clientId);
    const [clients, setClients] = useState([]);
    const [factures, setFactures] = useState([]);
    const [searchClient, setSearchClient] = useState('');
    const [showClientResults, setShowClientResults] = useState(false);
    const [formData, setFormData] = useState({
        id_client: clientId || '',
        client_nom: '',
        id_facture: factureId || '',
        date_reglement: new Date().toISOString().split('T')[0],
        montant: '',
        mode_reglement: 'especes',
        reference: '',
        commentaire: ''
    });
    const [errors, setErrors] = useState({});
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [clientInfo, setClientInfo] = useState(null);
    const [factureInfo, setFactureInfo] = useState(null);

    // Charger les clients au montage
    useEffect(() => {
        fetchClients();
    }, []);

    // Charger les données si clientId ou factureId est présent dans l'URL
    useEffect(() => {
        if (clientId) {
            loadClientById(clientId);
        }
        if (factureId) {
            loadFactureById(factureId);
        }
    }, [clientId, factureId]);

    // Charger les factures impayées quand un client est sélectionné
    useEffect(() => {
        if (formData.id_client) {
            fetchFacturesImpayees(formData.id_client);
        }
    }, [formData.id_client]);

    const loadClientById = async (id) => {
        try {
            setLoadingData(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setClientInfo(result.data);
                setFormData(prev => ({
                    ...prev,
                    id_client: result.data.id_client,
                    client_nom: result.data.raison_sociale
                }));
                setSearchClient(result.data.raison_sociale);
            }
        } catch (err) {
            console.error("Erreur chargement client", err);
        } finally {
            setLoadingData(false);
        }
    };

    const loadFactureById = async (id) => {
        try {
            setLoadingData(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/factures.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setFactureInfo(result.data);
                // Suggérer le montant du solde
                if (result.data && result.data.solde) {
                    setFormData(prev => ({
                        ...prev,
                        montant: result.data.solde
                    }));
                }
            }
        } catch (err) {
            console.error("Erreur chargement facture", err);
        } finally {
            setLoadingData(false);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            setClients(result.data || []);
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchFacturesImpayees = async (idClient) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/reglements.php?action=factures_impayees&id_client=${idClient}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            setFactures(result.data || []);
            
            // Si une facture était pré-sélectionnée dans l'URL
            if (factureId) {
                setFormData(prev => ({ ...prev, id_facture: factureId }));
            }
        } catch (err) {
            console.error("Erreur chargement factures", err);
        }
    };

    const handleClientSelect = (client) => {
        setFormData({
            ...formData,
            id_client: client.id_client,
            client_nom: client.raison_sociale,
            id_facture: '' // Réinitialiser la facture quand on change de client
        });
        setSearchClient(client.raison_sociale);
        setShowClientResults(false);
        setClientInfo(client);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        
        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.id_client) newErrors.id_client = "Veuillez sélectionner un client";
        if (!formData.montant) newErrors.montant = "Le montant est requis";
        else if (parseFloat(formData.montant) <= 0) newErrors.montant = "Le montant doit être supérieur à 0";
        if (!formData.mode_reglement) newErrors.mode_reglement = "Le mode de règlement est requis";
        
        if (formData.id_facture) {
            const facture = factures.find(f => f.id_facture === parseInt(formData.id_facture));
            if (facture && parseFloat(formData.montant) > facture.solde) {
                newErrors.montant = `Le montant ne peut pas dépasser le solde de la facture (${formatCurrency(facture.solde)})`;
            }
        }
        
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);
        setSubmitError('');

        try {
            const token = localStorage.getItem("token");
            const user = JSON.parse(localStorage.getItem("user") || "{}");

            const response = await fetch(`${API_BASE_URL}/reglements.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'create',
                    ...formData,
                    montant: parseFloat(formData.montant),
                    id_utilisateur: user.id_utilisateur || 1
                })
            });

            const result = await response.json();

            if (result.success) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    navigate('/ventes/reglements');
                }, 1500);
            } else {
                setSubmitError(result.message || "Erreur lors de la création");
            }
        } catch (err) {
            setSubmitError("Erreur de connexion au serveur");
        } finally {
            setLoading(false);
        }
    };

    const factureSelectionnee = factures.find(f => f.id_facture === parseInt(formData.id_facture)) || factureInfo;

    const modeOptions = [
        { value: 'especes', label: ' Espèces', icon: 'ri-money-euro-circle-line' },
        { value: 'cheque', label: ' Chèque', icon: 'ri-bank-card-line' },
        { value: 'virement', label: ' Virement bancaire', icon: 'ri-building-line' },
        { value: 'carte_bancaire', label: ' Carte bancaire', icon: 'ri-smartphone-line' },
        { value: 'traite', label: ' Traite', icon: 'ri-file-copy-line' }
    ];

    if (loadingData) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des données...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-bank-card-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Nouveau règlement</h4>
                                    <p className="text-muted small mb-0">
                                        {factureId ? 'Règlement pour une facture spécifique' : 'Saisissez un nouveau paiement client'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate(-1)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Bannière d'information si pré-rempli */}
                    {(factureId || clientId) && (
                        <Alert variant="info" className="mb-4 alert-modern">
                            <div className="d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                                    <i className="ri-information-line text-primary fs-5"></i>
                                </div>
                                <div>
                                    <strong className="d-block">Formulaire pré-rempli</strong>
                                    <p className="mb-0 small">
                                        {clientInfo && `Client: ${clientInfo.raison_sociale} `}
                                        {factureInfo && `- Facture: ${factureInfo.numero_facture} (Solde: ${formatCurrency(factureInfo.solde)})`}
                                    </p>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {submitSuccess && (
                        <Alert variant="success" className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            Règlement créé avec succès ! Redirection...
                        </Alert>
                    )}

                    {submitError && (
                        <Alert variant="danger" className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {submitError}
                        </Alert>
                    )}

                    <Card className="border-0 shadow-sm rounded-3">
                        <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                            <h5 className="mb-0 text-white">
                                <i className="ri-bank-card-line me-2"></i>
                                Informations de paiement
                            </h5>
                        </Card.Header>
                        <Card.Body className="p-4">
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        {/* Recherche client */}
                                        <Form.Group className="mb-4 position-relative">
                                            <Form.Label className="fw-semibold">Client <span className="text-danger">*</span></Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">
                                                    <i className="ri-user-line" style={{ color: '#21ACCD' }}></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Rechercher un client..."
                                                    value={searchClient}
                                                    onChange={(e) => {
                                                        setSearchClient(e.target.value);
                                                        setShowClientResults(true);
                                                    }}
                                                    onFocus={() => setShowClientResults(true)}
                                                    isInvalid={!!errors.id_client}
                                                    disabled={!!clientId}
                                                    className="border-2"
                                                />
                                            </InputGroup>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.id_client}
                                            </Form.Control.Feedback>
                                            
                                            {/* Résultats de recherche */}
                                            {showClientResults && searchClient && !clientId && (
                                                <div className="position-absolute w-100 bg-white border rounded-3 shadow-sm mt-1" style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto' }}>
                                                    {clients
                                                        .filter(c => 
                                                            c.raison_sociale?.toLowerCase().includes(searchClient.toLowerCase()) ||
                                                            c.code_client?.toLowerCase().includes(searchClient.toLowerCase())
                                                        )
                                                        .map(client => (
                                                            <div
                                                                key={client.id_client}
                                                                className="p-3 border-bottom hover-bg-light"
                                                                onClick={() => handleClientSelect(client)}
                                                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                                            >
                                                                <strong className="d-block">{client.raison_sociale}</strong>
                                                                <small className="text-muted">
                                                                    {client.code_client} | {client.telephone || 'Tél non renseigné'}
                                                                </small>
                                                            </div>
                                                        ))}
                                                </div>
                                            )}
                                        </Form.Group>

                                        {/* Infos client sélectionné */}
                                        {formData.id_client && (
                                            <div className="client-card mb-4 p-3 rounded-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            <div className="bg-primary bg-opacity-10 rounded-circle p-1">
                                                                <i className="ri-user-line text-primary" style={{ fontSize: '0.8rem' }}></i>
                                                            </div>
                                                            <strong className="text-primary">{formData.client_nom}</strong>
                                                        </div>
                                                        <small className="text-muted">
                                                            Code: {clients.find(c => c.id_client === parseInt(formData.id_client))?.code_client}
                                                        </small>
                                                    </div>
                                                    {!clientId && (
                                                        <Button 
                                                            variant="link" 
                                                            size="sm"
                                                            className="text-danger p-0"
                                                            onClick={() => {
                                                                setFormData({ ...formData, id_client: '', client_nom: '', id_facture: '' });
                                                                setSearchClient('');
                                                                setFactures([]);
                                                            }}
                                                        >
                                                            <i className="ri-close-line fs-5"></i>
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Sélection facture */}
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Facture</Form.Label>
                                            <Form.Select
                                                name="id_facture"
                                                value={formData.id_facture}
                                                onChange={handleChange}
                                                disabled={!formData.id_client || factures.length === 0 || !!factureId}
                                                className="border-2 rounded-3"
                                            >
                                                <option value="">Sélectionnez une facture (optionnel)</option>
                                                {factures.map(facture => (
                                                    <option key={facture.id_facture} value={facture.id_facture}>
                                                        {facture.numero_facture} - {formatCurrency(facture.solde)} restant
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {!formData.id_client && (
                                                <Form.Text className="text-muted small">
                                                    Sélectionnez d'abord un client
                                                </Form.Text>
                                            )}
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Date de règlement <span className="text-danger">*</span></Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">
                                                    <i className="ri-calendar-line" style={{ color: '#21ACCD' }}></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="date"
                                                    name="date_reglement"
                                                    value={formData.date_reglement}
                                                    onChange={handleChange}
                                                    required
                                                    className="border-2"
                                                />
                                            </InputGroup>
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Mode de règlement <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                name="mode_reglement"
                                                value={formData.mode_reglement}
                                                onChange={handleChange}
                                                isInvalid={!!errors.mode_reglement}
                                                required
                                                className="border-2 rounded-3"
                                            >
                                                {modeOptions.map(mode => (
                                                    <option key={mode.value} value={mode.value}>
                                                        {mode.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.mode_reglement}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Référence</Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">
                                                    <i className="ri-file-text-line" style={{ color: '#21ACCD' }}></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    type="text"
                                                    name="reference"
                                                    value={formData.reference}
                                                    onChange={handleChange}
                                                    placeholder="N° chèque, référence virement..."
                                                    className="border-2"
                                                />
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mt-2">
                                    <Col md={6}>
                                        {factureSelectionnee && (
                                            <div className="facture-summary-card mb-4">
                                                <div className="summary-header">
                                                    <i className="ri-file-list-3-line me-2"></i>
                                                    <span>Facture associée</span>
                                                </div>
                                                
                                                <div className="summary-body">
                                                    <div className="summary-row">
                                                        <span className="summary-label">
                                                            <i className="ri-barcode-line me-2"></i>
                                                            Numéro
                                                        </span>
                                                        <span className="summary-value fw-semibold">{factureSelectionnee.numero_facture}</span>
                                                    </div>
                                                    
                                                    <div className="summary-row">
                                                        <span className="summary-label">
                                                            <i className="ri-calculator-line me-2"></i>
                                                            Montant TTC
                                                        </span>
                                                        <span className="summary-value fw-semibold">{formatCurrency(factureSelectionnee.montant_ttc)}</span>
                                                    </div>
                                                    
                                                    <div className="summary-row">
                                                        <span className="summary-label">
                                                            <i className="ri-bank-card-line me-2"></i>
                                                            Déjà réglé
                                                        </span>
                                                        <span className="summary-value text-success fw-semibold">
                                                            <i className="ri-checkbox-circle-line me-1"></i>
                                                            {formatCurrency(factureSelectionnee.montant_regle || 0)}
                                                        </span>
                                                    </div>
                                                    
                                                    <div className="summary-divider"></div>
                                                    
                                                    <div className="summary-row highlight">
                                                        <span className="summary-label fw-bold">
                                                            <i className="ri-funds-line me-2"></i>
                                                            Solde restant
                                                        </span>
                                                        <span className="summary-value text-primary fw-bold fs-5">
                                                            {formatCurrency(factureSelectionnee.solde)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Montant <span className="text-danger">*</span></Form.Label>
                                            <InputGroup>
                                                <InputGroup.Text className="bg-light border-2">FCFA</InputGroup.Text>
                                                <Form.Control
                                                    type="number"
                                                    name="montant"
                                                    value={formData.montant}
                                                    onChange={handleChange}
                                                    placeholder="0"
                                                    min="0"
                                                    step="0.01"
                                                    isInvalid={!!errors.montant}
                                                    required
                                                    className="border-2"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.montant}
                                                </Form.Control.Feedback>
                                            </InputGroup>
                                            {factureSelectionnee && (
                                                <Form.Text className="text-muted small">
                                                    Solde de la facture: {formatCurrency(factureSelectionnee.solde)}
                                                </Form.Text>
                                            )}
                                        </Form.Group>

                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold">Commentaire</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="commentaire"
                                                value={formData.commentaire}
                                                onChange={handleChange}
                                                rows={3}
                                                placeholder="Informations complémentaires..."
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end gap-2 mt-4 pt-2">
                                    <Button 
                                        variant="outline-secondary" 
                                        onClick={() => navigate('/ventes/reglements')}
                                        className="rounded-pill px-4"
                                    >
                                        Annuler
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        variant="primary"
                                        disabled={loading}
                                        className="rounded-pill px-5"
                                        style={{ background: '#21ACCD', border: 'none' }}
                                    >
                                        {loading ? <Spinner size="sm" className="me-2" /> : <i className="ri-save-line me-2"></i>}
                                        Enregistrer le règlement
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            <style>{`
                .client-card {
                    transition: all 0.2s ease;
                }
                .client-card:hover {
                    border-color: #21ACCD !important;
                }
                
                .facture-summary-card {
                    background: linear-gradient(135deg, #f8faff 0%, #ffffff 100%);
                    border: 1px solid #e9ecef;
                    border-radius: 16px;
                    overflow: hidden;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                    transition: all 0.3s ease;
                }

                .facture-summary-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 12px 25px rgba(33, 172, 205, 0.1);
                }

                .summary-header {
                    background: linear-gradient(135deg, #21ACCD15, #1a8ba815);
                    padding: 12px 16px;
                    border-bottom: 1px solid #e9ecef;
                    font-weight: 600;
                    color: #2c3e50;
                    display: flex;
                    align-items: center;
                }

                .summary-header i {
                    color: #21ACCD;
                    font-size: 1.2rem;
                }

                .summary-body {
                    padding: 16px;
                }

                .summary-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 8px 0;
                    border-bottom: 1px dashed #e9ecef;
                }

                .summary-row:last-of-type {
                    border-bottom: none;
                }

                .summary-row.highlight {
                    background: #f1f5f9;
                    margin-top: 8px;
                    padding: 12px 16px;
                    border-radius: 12px;
                    border-bottom: none;
                }

                .summary-label {
                    display: flex;
                    align-items: center;
                    color: #5a6a7e;
                    font-size: 0.9rem;
                }

                .summary-label i {
                    font-size: 1rem;
                    color: #64748b;
                }

                .summary-value {
                    font-size: 1rem;
                }

                .summary-divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, #21ACCD30, transparent);
                    margin: 12px 0;
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .facture-summary-card {
                    animation: slideDown 0.3s ease-out;
                }

                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-20 {
                    font-size: 1.25rem;
                }
                
                .hover-bg-light:hover {
                    background-color: #f8fafc;
                }
            `}</style>
        </>
    );
}